'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Send, Smile, MicOff, Mic, Pin, Trash2, ShieldOff, X, ChevronDown } from 'lucide-react';

interface ChatMessage {
  id: string;
  user: string;
  msg: string;
  color: string;
  time: string;
  pinned?: boolean;
  blocked?: boolean;
}

const INITIAL_MESSAGES: ChatMessage[] = [
  { id: 'msg-001', user: 'usuaria_secreta', msg: 'jajajaja ese chisme 💀💀', color: 'text-primary', time: '21:28' },
  { id: 'msg-002', user: 'pelucona2000', msg: '¡Anfi cuenta el del mall!!!', color: 'text-secondary', time: '21:28' },
  { id: 'msg-003', user: 'chismosa_anon', msg: 'yo mandé ese chisme 🤭🤭', color: 'text-accent', time: '21:29' },
  { id: 'msg-004', user: 'tía_rosa_cl', msg: 'qué vergüenza ajena jajaj 😂', color: 'text-primary', time: '21:29' },
  { id: 'msg-005', user: 'anfi_fan_01', msg: '🧶🧶🧶 QUÉ TEJEDORAAA', color: 'text-secondary', time: '21:29' },
  { id: 'msg-006', user: 'vecina_chismosa', msg: 'yo conozco a ese del negocio 👀', color: 'text-primary', time: '21:30' },
  { id: 'msg-007', user: 'lurker_scl', msg: 'primera vez viendo el live y ya me enganchó', color: 'text-accent', time: '21:30' },
  { id: 'msg-008', user: 'carmen_belen', msg: '😱😱😱 DOS FAMILIAS???', color: 'text-secondary', time: '21:30' },
  { id: 'msg-009', user: 'anonima_prov', msg: 'mando mis chismes mañana jaja', color: 'text-primary', time: '21:30' },
  { id: 'msg-010', user: 'telenovela_fan', msg: '🔥🔥🔥 ESTO ES MEJOR QUE LA TELE', color: 'text-accent', time: '21:31' },
];

const COLORS = ['text-primary', 'text-secondary', 'text-accent'];

interface LiveChatProps {
  isHost?: boolean;
}

export default function LiveChat({ isHost = false }: LiveChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [chatMuted, setChatMuted] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [contextMenu, setContextMenu] = useState<{ msgId: string; x: number; y: number } | null>(null);
  const [showBlockedPanel, setShowBlockedPanel] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const pinnedMessages = messages.filter((m) => m.pinned && !m.blocked);
  const visibleMessages = messages.filter((m) => !m.blocked);

  useEffect(() => {
    bottomRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const showNotif = (text: string) => {
    setNotification(text);
    setTimeout(() => setNotification(null), 2500);
  };

  const handleSend = () => {
    if (!input?.trim() || chatMuted) return;
    const newMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      user: 'tú',
      msg: input?.trim(),
      color: 'text-primary',
      time: new Date()?.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages((prev) => [...prev, newMsg]);
    setInput('');
  };

  const handleRightClick = (e: React.MouseEvent, msgId: string) => {
    if (!isHost) return;
    e.preventDefault();
    setContextMenu({ msgId, x: e.clientX, y: e.clientY });
  };

  const deleteMessage = (msgId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== msgId));
    setContextMenu(null);
    showNotif('Mensaje eliminado');
  };

  const pinMessage = (msgId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, pinned: !m.pinned } : m))
    );
    setContextMenu(null);
    const msg = messages.find((m) => m.id === msgId);
    showNotif(msg?.pinned ? 'Mensaje desanclado' : 'Mensaje anclado 📌');
  };

  const blockUser = (msgId: string) => {
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    setBlockedUsers((prev) => [...prev, msg.user]);
    setMessages((prev) => prev.map((m) => (m.user === msg.user ? { ...m, blocked: true } : m)));
    setContextMenu(null);
    showNotif(`@${msg.user} bloqueado/a 🚫`);
  };

  const unblockUser = (username: string) => {
    setBlockedUsers((prev) => prev.filter((u) => u !== username));
    setMessages((prev) => prev.map((m) => (m.user === username ? { ...m, blocked: false } : m)));
    showNotif(`@${username} desbloqueado/a ✅`);
  };

  const unpinMessage = (msgId: string) => {
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, pinned: false } : m)));
    showNotif('Mensaje desanclado');
  };

  return (
    <div className="bg-card border border-border rounded-2xl flex flex-col h-full relative" style={{ minHeight: '560px', maxHeight: '700px' }}>
      {/* Notification toast */}
      {notification && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-50 bg-foreground text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg whitespace-nowrap">
          {notification}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm text-foreground">Chat en vivo</span>
          <span className={`w-2 h-2 rounded-full dot-pulse ${chatMuted ? 'bg-red-500' : 'bg-green-500'}`} />
          {chatMuted && <span className="text-xs text-red-500 font-semibold">SILENCIADO</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-tabular">1,247 viendo</span>
          {isHost && (
            <button
              onClick={() => { setChatMuted((v) => !v); showNotif(chatMuted ? 'Chat activado ✅' : 'Chat silenciado 🔇'); }}
              className={`p-1.5 rounded-lg transition-colors text-xs font-semibold flex items-center gap-1 ${chatMuted ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              title={chatMuted ? 'Activar chat' : 'Silenciar chat'}
            >
              {chatMuted ? <MicOff size={13} /> : <Mic size={13} />}
            </button>
          )}
        </div>
      </div>

      {/* Host controls bar */}
      {isHost && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border-b border-border shrink-0">
          <span className="text-xs font-bold text-primary uppercase tracking-wider">🎙 Controles</span>
          <div className="flex-1" />
          <button
            onClick={() => setShowBlockedPanel((v) => !v)}
            className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-colors ${showBlockedPanel ? 'bg-red-500/10 border-red-500/30 text-red-500' : 'border-border text-muted-foreground hover:text-foreground'}`}
          >
            <ShieldOff size={12} />
            Bloqueados ({blockedUsers.length})
            <ChevronDown size={11} className={`transition-transform ${showBlockedPanel ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {/* Blocked users panel */}
      {isHost && showBlockedPanel && (
        <div className="border-b border-border bg-red-500/5 px-3 py-2 shrink-0">
          {blockedUsers.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-1">Sin usuarios bloqueados</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {blockedUsers.map((u) => (
                <span key={u} className="flex items-center gap-1 bg-red-500/10 text-red-500 text-xs px-2 py-0.5 rounded-full">
                  @{u}
                  <button onClick={() => unblockUser(u)} className="hover:text-red-700 transition-colors" title="Desbloquear">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pinned messages */}
      {pinnedMessages.length > 0 && (
        <div className="border-b border-border bg-primary/5 px-3 py-2 shrink-0">
          {pinnedMessages.map((pm) => (
            <div key={pm.id} className="flex items-start gap-2">
              <Pin size={11} className="text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold text-primary">{pm.user}: </span>
                <span className="text-xs text-foreground/80 leading-relaxed">{pm.msg}</span>
              </div>
              {isHost && (
                <button onClick={() => unpinMessage(pm.id)} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" title="Desanclar">
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Chat muted overlay */}
      {chatMuted && isHost && (
        <div className="absolute inset-0 top-[88px] bg-background/60 backdrop-blur-[2px] z-20 flex flex-col items-center justify-center rounded-b-2xl gap-3">
          <MicOff size={32} className="text-red-500" />
          <p className="text-sm font-bold text-foreground">Chat silenciado</p>
          <p className="text-xs text-muted-foreground">Los espectadores no pueden enviar mensajes</p>
          <button
            onClick={() => { setChatMuted(false); showNotif('Chat activado ✅'); }}
            className="mt-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-xl hover:bg-primary/90 transition-colors"
          >
            Activar chat
          </button>
        </div>
      )}

      {/* Messages */}
      <div ref={chatRef} className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3 flex flex-col gap-2">
        {visibleMessages?.map((msg, i) => (
          <div
            key={msg?.id}
            onContextMenu={(e) => handleRightClick(e, msg.id)}
            className={`flex items-start gap-1.5 text-xs group relative rounded-lg px-1 py-0.5 transition-colors ${isHost ? 'hover:bg-muted/40 cursor-context-menu' : ''} ${msg.pinned ? 'bg-primary/5' : ''}`}
          >
            {msg.pinned && <Pin size={9} className="text-primary mt-1 shrink-0" />}
            <span className={`font-bold shrink-0 ${COLORS?.[i % COLORS?.length]}`}>{msg?.user}:</span>
            <span className="text-foreground/80 leading-relaxed flex-1">{msg?.msg}</span>
            {/* Inline host action buttons (visible on hover) */}
            {isHost && (
              <div className="hidden group-hover:flex items-center gap-1 shrink-0 ml-1">
                <button
                  onClick={() => pinMessage(msg.id)}
                  className={`p-0.5 rounded transition-colors ${msg.pinned ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                  title={msg.pinned ? 'Desanclar' : 'Anclar mensaje'}
                >
                  <Pin size={11} />
                </button>
                <button
                  onClick={() => deleteMessage(msg.id)}
                  className="p-0.5 rounded text-muted-foreground hover:text-red-500 transition-colors"
                  title="Eliminar mensaje"
                >
                  <Trash2 size={11} />
                </button>
                <button
                  onClick={() => blockUser(msg.id)}
                  className="p-0.5 rounded text-muted-foreground hover:text-red-500 transition-colors"
                  title="Bloquear usuario"
                >
                  <ShieldOff size={11} />
                </button>
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Context menu (right-click) */}
      {isHost && contextMenu && (
        <div
          className="fixed z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[160px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const msg = messages.find((m) => m.id === contextMenu.msgId);
            return (
              <>
                <button
                  onClick={() => pinMessage(contextMenu.msgId)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-foreground"
                >
                  <Pin size={13} className="text-primary" />
                  {msg?.pinned ? 'Desanclar mensaje' : 'Anclar mensaje'}
                </button>
                <button
                  onClick={() => deleteMessage(contextMenu.msgId)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-red-500"
                >
                  <Trash2 size={13} />
                  Eliminar mensaje
                </button>
                <div className="border-t border-border my-1" />
                <button
                  onClick={() => blockUser(contextMenu.msgId)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors text-red-500"
                >
                  <ShieldOff size={13} />
                  Bloquear a @{msg?.user}
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-3 flex items-center gap-2 shrink-0">
        <button className="p-1.5 text-muted-foreground hover:text-primary transition-colors shrink-0" aria-label="Emojis">
          <Smile size={18} />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e?.target?.value)}
          onKeyDown={(e) => e?.key === 'Enter' && handleSend()}
          placeholder={chatMuted ? 'Chat silenciado 🔇' : 'Di algo... 👀'}
          disabled={chatMuted && !isHost}
          className="flex-1 bg-input border border-border rounded-xl px-3 py-2 text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          onClick={handleSend}
          disabled={!input?.trim() || (chatMuted && !isHost)}
          className="p-2 bg-primary text-white rounded-xl disabled:opacity-40 hover:bg-primary/90 transition-colors shrink-0"
          aria-label="Enviar mensaje"
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}